export type HomeFeatureKey =
  | 'multiCity'
  | 'dayByDay'
  | 'weather'
  | 'options'
  | 'aiPlanning'
  | 'shareIn'
  | 'pdf'
  | 'localFirst';

export interface HomeFeature {
  key: HomeFeatureKey;
  title: string;
  description: string;
}

export interface HomeStep {
  title: string;
  description: string;
}

export interface HomeWorkflowCard {
  title: string;
  description: string;
}

export interface HomeImageBreak {
  imageSrc: string;
  eyebrow: string;
  title: string;
  description: string;
}

export const homeCopyEn = {
  hero: {
    eyebrow: 'WandererTrip',
    title: 'Your trip, organized—day by day.',
    subtitle:
      'Create multi-city trips with daily plans alongside weather. Capture confirmations and links fast, keep an “options” list per city, export a shareable PDF itinerary, and (optionally) use AI to generate a city plan or extract trip details from what you share into the app.',
    primaryCta: 'Explore what it does',
    secondaryCta: 'See features',
    imageSrc: '/images/photo-1597891387547-99bb301e98ef.jpeg',
  },
  sections: {
    features: {
      title: 'Built for real travel',
      subtitle:
        'Plan by city and date, then work day-by-day with weather, lodging, and everything you need in one place.',
    },
    howItWorks: {
      title: 'How it works',
      subtitle: 'A simple flow that keeps planning clean and easy to update.',
    },
    workflows: {
      title: 'Core workflows',
      subtitle: 'The key things WandererTrip is designed to do well.',
    },
  },
  features: [
    {
      key: 'multiCity',
      title: 'Multi-city trips with dates',
      description: 'Add multiple city visits (and cruise ports when needed) and keep your trip date range consistent.',
    },
    {
      key: 'aiPlanning',
      title: 'Optional AI city planning',
      description: 'Generate highlights and time-blocked plans, then add suggestions into your trip (with conflict checks).',
    },
    {
      key: 'shareIn',
      title: 'Share into the app',
      description: 'Send URLs, text, and files to a shared inbox and (optionally) extract trip details before saving.',
    },
    {
      key: 'dayByDay',
      title: 'Day view itinerary',
      description: 'See a selected day with weather, scheduled items (meals/activities/transport/lodging), notes, and tonight’s stay.',
    },
    {
      key: 'weather',
      title: 'Daily + hourly weather',
      description: 'Forecasts per city visit, cached locally so your itinerary stays useful on the go.',
    },
    {
      key: 'options',
      title: 'Per-city “Options” lists',
      description: 'Keep a checklist of ideas per city: reorder, edit, mark done, and delete.',
    },
    {
      key: 'pdf',
      title: 'PDF itinerary export',
      description: 'Generate and share a trip itinerary PDF when you need to send plans to someone else.',
    },
    {
      key: 'localFirst',
      title: 'Local-first by default',
      description: 'Trip data lives on your device. Network calls are used only for weather, AI, and optional community features.',
    },
  ] satisfies HomeFeature[],
  imageBreaks: [
    {
      imageSrc: '/images/well_traveled.jpg',
      eyebrow: 'Plan smarter (optional)',
      title: 'Let AI draft the first version.',
      description: 'Generate city highlights and time-blocked plans, then keep only what you want.',
    },
    {
      imageSrc: '/images/photo-1706971125381-d0680c12adf8.jpeg',
      eyebrow: 'Day-by-day planning',
      title: 'See what’s happening on each day.',
      description: 'Weather + itinerary in one view, so you can plan around conditions without bouncing between apps.',
    },
    {
      imageSrc: '/images/photo-1596241913242-b20788b3dfeb.jpeg',
      eyebrow: 'Capture fast',
      title: 'Share confirmations into your trip.',
      description: 'Drop links, text, PDFs, and files into a shared inbox, then save the important details into the right city.',
    },
  ] satisfies HomeImageBreak[],
  howItWorks: {
    steps: [
      {
        title: 'Create a trip + add city visits',
        description: 'Set arrival/departure dates for each stop. We keep the trip’s day range consistent.',
      },
      {
        title: 'Plan day-by-day',
        description: 'Add lodging, meals, activities, transport, and notes. Keep “Options” lists per city for ideas.',
      },
      {
        title: 'Capture and share',
        description: 'Share items into the app, and export a PDF itinerary when it’s time to send or print.',
      },
    ] satisfies HomeStep[],
  },
  workflows: [
    {
      title: 'Trip → city visits → days',
      description:
        'Plan multi-city trips with a clean structure that stays consistent as dates shift and stops change.',
    },
    {
      title: 'AI planning and review',
      description:
        'Generate a city plan, then add individual items or bulk-save a day—while avoiding time conflicts.',
    },
    {
      title: 'Shared Items inbox',
      description:
        'Send content from other apps into WandererTrip and optionally extract structured details before saving.',
    },
    {
      title: 'Share itinerary as PDF',
      description: 'Export and share a PDF itinerary so everyone has the plan, even if they’re not using the app.',
    },
  ] satisfies HomeWorkflowCard[],
  finalCta: {
    title: 'Ready to plan your next trip?',
    subtitle: 'Start with your cities and dates. WandererTrip will keep the plan and the forecast together.',
    cta: 'See features',
  },
} as const;

